/**
 * CreateProjectCard — the Director's "Create new project" proposal card.
 *
 * Rendered when the Director calls `propose_new_project` (delivered as a
 * UserQuestion with metadata.render_as === 'create_project_button'). The user
 * must explicitly click "Create" — the Director never creates autonomously.
 * Presentational: navigation/creation logic is in the parent (AgentChat).
 */
import { memo } from 'react'
import { useTranslation } from 'react-i18next'

export interface CreateProjectCardProps {
  name: string
  carryoverCount: number
  onCreate: () => void
  onDismiss: () => void
  disabled?: boolean
}

export const CreateProjectCard = memo(function CreateProjectCard({
  name,
  carryoverCount,
  onCreate,
  onDismiss,
  disabled = false,
}: CreateProjectCardProps) {
  const { t } = useTranslation('chat')
  return (
    <div className="rounded-lg border border-border bg-surface-1 p-4" data-testid="create-project-card">
      <h3 className="text-sm font-medium text-text-primary mb-1">{name}</h3>
      {carryoverCount > 0 && (
        <p className="text-xs font-medium text-text-muted mb-3" data-testid="create-project-carryover">
          {t('createProject.carryover', { count: carryoverCount })}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          disabled={disabled}
          onClick={onCreate}
          data-testid="create-project-confirm"
          // Near-white fill -> dark label. `text-white` worked on the amber it
          // used to sit on; on `chrome` it would be white on white.
          className="px-3 py-1.5 rounded-md text-xs font-medium bg-chrome text-chrome-foreground hover:bg-chrome/90 focus-visible:ring-2 focus-visible:ring-chrome/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {t('createProject.create')}
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={onDismiss}
          data-testid="create-project-dismiss"
          className="px-3 py-1.5 rounded-md text-xs font-medium text-text-muted hover:text-text-secondary hover:bg-surface-2 focus-visible:ring-2 focus-visible:ring-border disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {t('createProject.notNow')}
        </button>
      </div>
    </div>
  )
})
